import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectDetail } from '../../lib/queries';
import { useEnvironment } from '../../lib/queries';
import { api, errorText } from '../../lib/api';
import { toast } from '../../lib/store';
import { Spinner } from '../../components/ui';
import { qk } from '../../lib/keys';

export default function HostingPage({ project }: { project: ProjectDetail }) {
  const query = useEnvironment(project.id);
  const qc = useQueryClient();
  const env = query.data?.environment;
  const [busy, setBusy] = useState(false);
  const owner = project.role === 'owner';

  const act = async (action: 'retry' | 'pause' | 'resume' | 'undo-delete' | 'delete-now') => {
    setBusy(true);
    try {
      await api.post(`/api/projects/${project.id}/environment/${action}`);
      await query.refetch();
      await qc.invalidateQueries({ queryKey: qk.projects });
      toast(action === 'retry' ? 'Provisioning restarted' : action === 'pause' ? 'Project paused' : action === 'resume' || action === 'undo-delete' ? 'Project resuming' : 'Provider resources deleted');
    } catch (error) {
      toast(errorText(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return <section className="card settings-card">
    <h2>Hosting</h2>
    {!env ? <p className="muted">This project uses its existing server connection. Automatic Supabase and Daytona hosting applies to newly created hosted projects.</p> : <>
      <div className="status-line"><b>Environment</b><span style={{ flex: 1 }} />{env.state.replaceAll('_', ' ')}</div>
      <p className="muted">Current step: {env.step.replaceAll('_', ' ')}</p>
      {env.errorDetail && <div className="err">{env.errorDetail}</div>}
      <div className="ruled-grid">
        <div><b>Supabase project</b><div className="mono">{env.supabaseRef ?? 'Provisioning…'}{env.provisioningMode === 'shared' ? ' (shared)' : ''}</div>{env.supabaseRef && <a href={`https://supabase.com/dashboard/project/${env.supabaseRef}`} target="_blank" rel="noreferrer">Open Supabase dashboard ↗</a>}</div>
        {env.provisioningMode === 'shared' && <div><b>Private app schema</b><div className="mono">{env.appSchema ?? 'Provisioning…'}</div></div>}
        <div><b>Daytona sandbox</b><div className="mono">{env.daytonaSandboxId ?? 'Provisioning…'}</div></div>
        <div><b>Region</b><div>{env.supabaseRegion ?? '—'}</div></div>
      </div>
      {owner && <div className="row" style={{ marginTop: 16 }}>
        {env.state === 'failed' && <button className="btn btn-secondary" disabled={busy} onClick={() => void act('retry')}>{busy && <Spinner />}Retry</button>}
        {env.state === 'ready' && <button className="btn btn-secondary" disabled={busy} onClick={() => void act('pause')}>{busy && <Spinner />}Pause</button>}
        {env.state === 'paused' && <button className="btn btn-secondary" disabled={busy} onClick={() => void act('resume')}>{busy && <Spinner />}Resume</button>}
        {env.state === 'deleting' && <><button className="btn btn-secondary" disabled={busy} onClick={() => void act('undo-delete')}>{busy && <Spinner />}Undo deletion</button><button className="btn btn-secondary btn-danger" disabled={busy} onClick={() => void act('delete-now')}>Delete now</button></>}
      </div>}
      {env.state === 'deleting' && <p className="muted">{env.provisioningMode === 'shared' ? 'This app’s database login and Daytona sandbox are stopped. The shared Supabase project stays online.' : 'Provider resources are paused.'} They will be removed seven days after deletion was scheduled. You can undo deletion here until then.</p>}
      <h3 style={{ marginTop: 24 }}>Provisioning history</h3>
      <ol>{query.data?.events.map((event, i) => <li key={i}>{new Date(event.at).toLocaleString()} · {event.step.replaceAll('_', ' ')} · {event.outcome}{event.detail ? ` · ${event.detail}` : ''}</li>)}</ol>
    </>}
  </section>;
}
